import React, { useState, useRef, useEffect } from 'react';

const ResponsiveImage = ({
  src,
  alt,
  className = '',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
  onClick,
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Import image manifest for optimized images
  const getOptimizedImages = (originalSrc) => {
    try {
      // Dynamic import for better error handling
      let imageManifest;
      try {
        imageManifest = require('../imageManifest.json');
      } catch (e) {
        // Fallback if manifest doesn't exist
        return null;
      }
      
      // Normalize the src path to match manifest keys
      let manifestKey = originalSrc;
      if (manifestKey.startsWith('/pics/')) {
        manifestKey = manifestKey.substring('/pics/'.length);
      }
      if (manifestKey.startsWith('pics/')) {
        manifestKey = manifestKey.substring('pics/'.length);
      }
      
      // Try to find the image in the manifest with various path formats
      let imageData = imageManifest[manifestKey];
      
      // Try Windows path format (backslashes) if not found
      if (!imageData) {
        const windowsKey = manifestKey.replace(/\//g, '\\');
        imageData = imageManifest[windowsKey];
      }
      
      // Try URL decoded version if not found
      if (!imageData) {
        try {
          const decodedKey = decodeURIComponent(manifestKey);
          imageData = imageManifest[decodedKey];
          
          if (!imageData) {
            const decodedWindowsKey = decodedKey.replace(/\//g, '\\');
            imageData = imageManifest[decodedWindowsKey];
          }
        } catch (e) {
          // Ignore decoding errors
        }
      }

      if (imageData) {
        const normalizePath = (p) => p ? p.replace(/\\/g, '/') : '';

        return {
          blur: imageData.blur,
          srcSet: {
            webp: [
              `${normalizePath(imageData.sizes.thumbnail.webp)} 300w`,
              `${normalizePath(imageData.sizes.medium.webp)} 800w`,
              `${normalizePath(imageData.sizes.large.webp)} 1600w`
            ].join(', '),
            jpg: [
              `${normalizePath(imageData.sizes.thumbnail.jpg)} 300w`,
              `${normalizePath(imageData.sizes.medium.jpg)} 800w`,
              `${normalizePath(imageData.sizes.large.jpg)} 1600w`
            ].join(', ')
          },
          fallback: normalizePath(imageData.sizes.medium.jpg)
        };
      }
    } catch (e) {
      // Manifest not available or image not found
    }
    
    return null;
  };

  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setError(true);
  };

  const optimizedImages = getOptimizedImages(src);
  
  // If we have optimized images, use them
  if (optimizedImages && isInView) {
    return (
      <div 
        ref={imgRef}
        className={`relative overflow-hidden ${className}`}
        style={style}
        onClick={onClick}
        {...props}
      >
        {/* Blur placeholder */}
        {!isLoaded && (
          <img
            src={optimizedImages.blur}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-110"
            style={{ transition: 'opacity 0.3s ease-in-out' }}
          />
        )}
        
        {/* Main optimized image with WebP support */}
        <picture>
          <source
            srcSet={optimizedImages.srcSet.webp}
            sizes={sizes}
            type="image/webp"
          />
          <source
            srcSet={optimizedImages.srcSet.jpg}
            sizes={sizes}
            type="image/jpeg"
          />
          <img
            src={optimizedImages.fallback}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleLoad}
            onError={handleError}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
          />
        </picture>
        
        {/* Loading indicator */}
        {!isLoaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          </div>
        )}
        
        {/* Error fallback */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
            <div className="text-gray-500 text-center">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">Image unavailable</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback to original image with lazy loading
  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={style}
      onClick={onClick}
      {...props}
    >
      {isInView ? (
        <>
          <img
            src={src}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleLoad}
            onError={handleError}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
          />
          
          {/* Loading indicator */}
          {!isLoaded && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          )}
          
          {/* Error fallback */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <div className="text-gray-500 text-center">
                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Image unavailable</p>
              </div>
            </div>
          )}
        </>
      ) : (
        // Placeholder while not in view
        <div className="w-full h-full bg-gray-200 animate-pulse" />
      )}
    </div>
  );
};

export default ResponsiveImage;